import { computed } from "mobx";
import { getPlatform } from "../../utils/StringUtilities";
import { sourceTypesEnum } from "../../utils/SourceUtils";
import { isFacebookImageExpired } from "../../utils/FacebookUtils";
import { getCdnUrl } from "../../utils/SchoolBlocksUtilities";
import { ReactiveItem } from "./ReactiveItem";

export interface IReactiveContentItem extends ContentItem {
  update: (obj: { [v: string]: any }) => void;
  image: string;
  isExternalGoogleResult?: boolean;
}

export function isEvent(
  item: EventContentItem | ContentItem,
): item is EventContentItem {
  return (item as EventContentItem).is_event === true;
}

export function isMedia(
  item: MediaContentItem | ContentItem,
): item is MediaContentItem {
  return (item as MediaContentItem).is_media === true;
}

export function isFile(
  item: FileContentItem | ContentItem,
): item is FileContentItem {
  return (item as FileContentItem).is_file === true;
}

export function isNews(item: ContentItem): item is NewsContentItem {
  return item.is_news === true;
}

export function isCoursework(
  item: CourseWorkContentItem | ContentItem,
): item is CourseWorkContentItem {
  return item.is_coursework === true;
}

export type IReactiveNewsContentItem = IReactiveContentItem &
  (NewsContentItem | MediaContentItem | FileContentItem);
export type IReactiveEventContentItem = IReactiveContentItem & EventContentItem;
export type IReactiveFileContentItem = IReactiveContentItem & FileContentItem;
export type IReactiveFolderContentItem = IReactiveContentItem &
  FolderContentItem;
export type IReactiveMediaContentItem = IReactiveContentItem & MediaContentItem;
export type IReactiveCourseWorkContentItem = IReactiveContentItem &
  CourseWorkContentItem;
export type SchoolFeedContentItem =
  | IReactiveEventContentItem
  | IReactiveNewsContentItem
  | IReactiveFileContentItem
  | IReactiveMediaContentItem
  | IReactiveCourseWorkContentItem;

export type ContentItemType =
  | NewsContentItem
  | MediaContentItem
  | FileContentItem
  | FolderContentItem
  | EventContentItem
  | CourseWorkContentItem;

export class ReactiveContentItem
  extends ReactiveItem<ContentItemType>
  implements ContentItem
{
  // `declare` prevents TypeScript from emitting field initializers that would run
  // after super() and conflict with MobX observable properties set by the base class.
  declare organization: OrganizationTypeOrganization;
  declare id: number;
  declare source;
  declare third_party_id;
  declare third_party_url;
  declare type;
  declare edited;
  declare is_coursework;
  declare is_event;
  declare is_news;
  declare is_media;
  declare is_description;
  declare is_location;
  declare is_file;
  declare is_folder;
  declare is_review;
  declare is_quicklink;
  declare is_custom;
  declare json_data;
  declare created_at;
  declare updated_at;
  declare organization_id;
  declare source_id;
  declare published;
  declare search_vector;
  declare geo_point;
  declare meritchat_enabled;
  declare language;
  declare discussion_prompt;

  constructor(item: ContentItemType) {
    super(item, {
      image: computed,
      link: computed,
      linkText: computed,
    });
  }

  protected excludedKeys(): string[] {
    return ["title", "description"];
  }

  get image() {
    return getItemImage(this.item);
  }

  get altText() {
    return getAltText(this.item);
  }

  get images(): MediaImage[] {
    if (isNews(this.item)) {
      return this.item.json_data.news?.images ?? [];
    }
    return (this.item.json_data as { images?: MediaImage[] })?.images ?? [];
  }

  get title() {
    // Display the description as the title for Serp API. Why wouldn't we just populate the title field with the
    // contents of the description? Because we still need the value from title to use in the Read More on... button
    if (getPlatform(this.type) === sourceTypesEnum.SERP_API) {
      return this.item.description;
    }
    return this.item.title;
  }

  set title(title) {
    this.item.title = title;
  }

  get description() {
    if (getPlatform(this.type) === sourceTypesEnum.SERP_API) {
      return "";
    }
    return this.item.description;
  }

  set description(description) {
    this.item.description = description;
  }

  get link() {
    if (this.item.third_party_url) {
      return this.item.third_party_url;
    } else if (isNews(this.item) && this.item.json_data.news.topic_url) {
      return this.item.json_data.news.topic_url;
    } else {
      return undefined;
    }
  }

  get linkText() {
    if (this.item.third_party_url) {
      const attribution = getPlatform(this.item.type);
      let attributionTitle = "";
      if (
        this.item.source?.account_title !== undefined &&
        attribution === sourceTypesEnum.RSS
      ) {
        attributionTitle = this.item.source.account_title;
      } else if (attribution === sourceTypesEnum.SERP_API) {
        attributionTitle = this.item.title;
      } else if (attribution === sourceTypesEnum.PARENTSQUARE) {
        return "View on ParentSquare";
      } else {
        attributionTitle = attribution;
      }
      let linkTitle = ` on ${attributionTitle.toLowerCase()}`;
      return `View ${linkTitle}`;
    } else if (isNews(this.item) && this.item.json_data.news.topic_url_text) {
      return this.item.json_data.news.topic_url_text;
    } else {
      return undefined;
    }
  }
}

export function getAltText(item: ContentItem) {
  const isImage = /photo/.test(item.type);
  let text;

  if (isNews(item)) {
    text = item.json_data.news.cover_image_alt_text;
  } else if (isEvent(item)) {
    text = item.json_data.event.full_picture_alt_text;
  } else if (
    isMedia(item) &&
    isImage &&
    item.json_data.images &&
    item.json_data.images.length > 0
  ) {
    text = item.json_data.images[item.json_data.images.length - 1].alt_text;
  } else if (isMedia(item) && item.json_data.videos) {
    text = item.json_data.videos.videos_list[0].alt_text;
  } else if (isFile(item) && item.json_data.file.cover_image_url) {
    text = item.json_data.file.cover_image_alt_text;
  }

  if (!text) {
    if (item.is_custom) {
      text = item.title;
    } else {
      text = `Image from ${getPlatform(item.type)}`;
    }
  }

  return text;
}

function convertGoogleUrl(originalUrl: string) {
  const fileId = originalUrl.match(/\/uc\?id=(.*)/);
  if (fileId && fileId.length > 1) {
    return `https://drive.google.com/thumbnail?id=${fileId[1]}`;
  }
  return originalUrl;
}

/**
 * @param item -
 * @param useSourceImageDefault - default to item's content_source image if item doesn't have its own image
 */
export function getItemImage(item: ContentItem, useSourceImageDefault = true) {
  const isImage = /photo/.test(item.type);
  const platform = getPlatform(item.type);

  let image: string | null | undefined = null;

  // first try to get the image from the content item json_data
  if (isNews(item) && item.json_data.news.cover_image_url) {
    image = item.json_data.news.cover_image_url;
  } else if (
    isEvent(item) &&
    item.json_data.event &&
    item.json_data.event.full_picture
  ) {
    image = item.json_data.event.full_picture;
  } else if (
    isMedia(item) &&
    isImage &&
    item.json_data.images &&
    item.json_data.images.length > 0
  ) {
    image = item.json_data.images[item.json_data.images.length - 1].raw_url;
  } else if (isMedia(item) && item.json_data.videos) {
    const placeholder = require("../../assets/images/placeholders/placeholder-video.png");
    image = item.json_data.videos.cover_image_url || placeholder;
  } else if (isFile(item) && item.json_data.file.cover_image_url) {
    image = item.json_data.file.cover_image_url;
  }
  // make sure image URL isn't expired if it's from Facebook - if it is, we need to continue onward
  if (platform === sourceTypesEnum.FACEBOOK && isFacebookImageExpired(image)) {
    image = null;
  }
  if (platform === sourceTypesEnum.GOOGLE_DRIVE && image) {
    image = convertGoogleUrl(image);
  }

  // if we still don't have an image, try to extract from content source, making sure that if it's from Facebook it isn't expired
  if (
    !image &&
    useSourceImageDefault &&
    item.source &&
    item.source.account_image &&
    !(
      platform === sourceTypesEnum.FACEBOOK &&
      isFacebookImageExpired(item.source.account_image)
    ) &&
    platform !== sourceTypesEnum.GOOGLE_DRIVE
  ) {
    if (platform === sourceTypesEnum.TWITTER) {
      // use larger version of profile picture
      image = item.source.account_image.replace("_normal", "");
    } else {
      image = item.source.account_image;
    }
  }

  if (item.is_custom || platform == sourceTypesEnum.SERP_API) {
    image = getCdnUrl(image || "");
  }

  return image;
}
